const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

function isStorageAvailable() {
  return new Promise((resolve) => {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      resolve(true);
    } catch (e) {
      // Storage is not available (e.g., incognito mode, blocked cookies)
      resolve(false);
    }
  });
}

async function canRegisterServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers are not supported in this browser');
    return false;
  }

  // Check if storage is available
  const storageAvailable = await isStorageAvailable();
  if (!storageAvailable) {
    console.warn('Storage is not available. Service worker registration skipped.');
    console.warn('This may happen in:');
    console.warn('  - Incognito/Private browsing mode');
    console.warn('  - When third-party cookies are blocked');
    console.warn('  - When storage quota is exceeded');
    console.warn('  - In Safari with ITP enabled');
    return false;
  }

  return true;
}

export function register(config) {
  // Check if we can register the service worker
  canRegisterServiceWorker().then((canRegister) => {
    if (!canRegister) {
      // Execute onSuccess callback even without service worker (progressive enhancement)
      if (config && config.onSuccess) {
        config.onSuccess(null);
      }
      return;
    }

    // Option 2: Only warn about HTTPS for non-localhost domains
    if (process.env.NODE_ENV !== 'production' && !isLocalhost && window.location.protocol !== 'https:') {
      console.warn('Service workers require HTTPS to function properly in production');
    }

    const publicUrl = new URL(process.env.PUBLIC_URL || '/', window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      console.warn('Service worker will not work: different origins');
      return;
    }

    window.addEventListener('load', () => {
      // Use absolute path for service worker
      const swUrl = `${window.location.origin}${process.env.PUBLIC_URL || ''}/sw.js`;
      console.log('Service worker URL:', swUrl);
      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://cra.link/PWA'
          );
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }).catch((error) => {
    console.error('Error checking service worker availability:', error);
    // Fallback: try to register anyway
    if (config && config.onSuccess) {
      config.onSuccess(null);
    }
  });
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl, {
      // Specify scope explicitly
      scope: process.env.PUBLIC_URL || '/'
    })
    .then((registration) => {
      console.log('SW registered: ', registration);

      // Force update check on load
      registration.update();

      // Re-check updates when tab is focused
      window.addEventListener("focus", () => {
        registration.update();

        // If service worker lost, reload app
        if (!navigator.serviceWorker.controller) {
          console.warn("Lost SW controller, reloading...");
          window.location.reload();
        }
      });
      
      setInterval(() => {
        console.log('Checking for SW updates...');
        registration.update();
      }, 1000 * 60); // Check every minute

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
            if (navigator.serviceWorker.controller) {
              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              console.log('Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);

      if (error.message && error.message.includes('Failed to access storage')) {
        console.warn('Service worker registration failed due to storage issues.');
        console.warn('The app will work but without offline capabilities.');
      }

      if (config && config.onSuccess) {
        config.onSuccess(null);
      }
    });
}

function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
    cache: 'no-cache' // Ensure we get fresh content
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch((error) => {
      console.warn("SW fetch failed, running in offline mode:", error);

      // Try again when tab regains focus
      window.addEventListener(
        "focus",
        () => {
          console.log("Retrying SW registration after focus...");
          registerValidSW(swUrl, config);
        },
        { once: true }
      );
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}